import React from 'react';
import styled from 'styled-components';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { Typography } from '../../Typography';

interface UploadItemBaseProps {
  fileName: string;
  error?: boolean;
  className?: string;
}

interface RemovableUploadItemProps extends UploadItemBaseProps {
  onRemove: () => void;
  removeLabel: string;
}

interface ReadOnlyUploadItemProps extends UploadItemBaseProps {
  onRemove?: never;
  removeLabel?: never;
}

export type UploadItemProps =
  | RemovableUploadItemProps
  | ReadOnlyUploadItemProps;

export const UploadItem: React.FC<UploadItemProps> = ({
  fileName,
  error = false,
  onRemove,
  removeLabel,
  className
}) => {
  const nameColor = error
    ? theme.dime.colors.text.critical
    : theme.dime.colors.link.primary;
  const iconColor = error
    ? theme.dime.colors.icon.critical
    : theme.dime.colors.icon.primary;

  return (
    <Row $error={error} className={className} data-testid="upload-item">
      <Name title={fileName}>
        <IconWrap
          type={IconType.PaperClip}
          size={IconSize.Small}
          style={{ color: iconColor, flexShrink: 0 }}
        />
        <FileName
          variant="body1"
          color={nameColor}
          data-testid="upload-item-name"
        >
          {fileName}
        </FileName>
      </Name>
      {onRemove ? (
        <RemoveButton
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          data-testid="upload-item-remove"
        >
          <IconWrap
            type={IconType.Remove}
            size={IconSize.Small}
            style={{ color: iconColor }}
          />
        </RemoveButton>
      ) : null}
    </Row>
  );
};

const FileName = styled(Typography)`
  && {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: ${theme.dime.fonts.lineHeights.md};
  }
`;

const RemoveButton = styled.button`
  display: flex;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const Row = styled.div<{ $error: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.dime.spacing.m};
  padding: ${theme.dime.spacing.m} ${theme.dime.spacing['2m']};
  border-radius: ${theme.dime.radius.m};
  box-shadow: inset 0 0 0 1px ${theme.dime.colors.border.static};
  width: 100%;

  ${RemoveButton} {
    opacity: ${({ $error }) => ($error ? 1 : 0)};
  }

  &:hover,
  &:focus-within {
    background: ${theme.dime.colors.fill.secondary};

    ${RemoveButton} {
      opacity: 1;
    }
  }
`;

const Name = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${theme.dime.spacing.m};
  min-width: 0;
`;
